import { Check, Clock, X } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { Box, Text } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { PendingApprovalAction } from '../types';
import { getToolFriendlyLabel } from '../utils/toolLabels';
import {
  type ApprovalCompany,
  CompanyApprovalRows,
  readApprovalCompanies,
} from './CompanyApprovalRows';

interface PendingActionCardProps {
  readonly action: PendingApprovalAction;
  readonly isResolving?: boolean;
  readonly onApprove: (itemIds?: readonly string[]) => void;
  readonly onReject: () => void;
}

function formatRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

interface ActionInputSummaryProps {
  readonly toolName: string;
  readonly input: Record<string, unknown>;
}

// Renders the safe, already-redacted `action.input` payload the backend
// persists on PendingApprovalAction so a human can see what they're
// approving instead of blindly trusting the tool label (HITL safeguard).
function ActionInputSummary({ toolName, input }: ActionInputSummaryProps) {
  if (toolName === 'send_whatsapp_message') {
    return <WhatsAppApprovalSummary input={input} />;
  }
  return <GenericApprovalSummary input={input} />;
}

function WhatsAppApprovalSummary({ input }: { input: Record<string, unknown> }) {
  const contactLast4 = typeof input.contactLast4 === 'string' ? input.contactLast4 : undefined;
  const body = typeof input.body === 'string' ? input.body : undefined;
  const templateId = typeof input.templateId === 'string' ? input.templateId : undefined;

  if (!contactLast4 && !body && !templateId) return null;

  return (
    <Box style={styles.summary}>
      {contactLast4 ? (
        <Text variant="caption" color={colors.text.secondary} style={styles.summaryLine}>
          {`To: ••••${contactLast4}`}
        </Text>
      ) : null}
      {body ? (
        <Text
          variant="caption"
          color={colors.text.secondary}
          style={styles.summaryLine}
          numberOfLines={4}
        >
          &quot;{body}&quot;
        </Text>
      ) : null}
      {templateId ? (
        <Text variant="caption" color={colors.text.tertiary} style={styles.summaryLine}>
          Template: {templateId}
        </Text>
      ) : null}
    </Box>
  );
}

function GenericApprovalSummary({ input }: { input: Record<string, unknown> }) {
  const entries = Object.entries(input);
  if (entries.length === 0) return null;

  return (
    <Box style={styles.summary}>
      {entries.map(([key, value]) => (
        <Text
          key={key}
          variant="caption"
          color={colors.text.secondary}
          style={styles.summaryLine}
          numberOfLines={2}
        >
          {formatSummaryKey(key)}: {formatSummaryValue(value)}
        </Text>
      ))}
    </Box>
  );
}

function formatSummaryKey(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatSummaryValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.map((item) => formatSummaryValue(item)).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

interface ApprovalButtonsProps {
  readonly approveLabel: string;
  readonly rejectLabel: string;
  readonly disabled: boolean;
  readonly approveDisabled?: boolean;
  readonly onApprove: () => void;
  readonly onReject: () => void;
}

function ApprovalButtons({
  approveLabel,
  rejectLabel,
  disabled,
  approveDisabled = disabled,
  onApprove,
  onReject,
}: ApprovalButtonsProps) {
  return (
    <Box flexDirection="row" gap="sm" style={styles.actions}>
      <Pressable
        onPress={onApprove}
        disabled={approveDisabled}
        accessibilityRole="button"
        accessibilityLabel={approveLabel}
        accessibilityState={{ disabled: approveDisabled }}
        hitSlop={8}
        style={({ pressed }) => [
          styles.button,
          styles.approveButton,
          (pressed || approveDisabled) && styles.buttonDisabled,
        ]}
      >
        <Check size={14} color={colors.status.success.main} strokeWidth={2.5} />
        <Text variant="caption" color={colors.status.success.main} style={styles.buttonLabel}>
          {approveLabel}
        </Text>
      </Pressable>
      <Pressable
        onPress={onReject}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={rejectLabel}
        accessibilityState={{ disabled }}
        hitSlop={8}
        style={({ pressed }) => [
          styles.button,
          styles.rejectButton,
          (pressed || disabled) && styles.buttonDisabled,
        ]}
      >
        <X size={14} color={colors.status.error.main} strokeWidth={2.5} />
        <Text variant="caption" color={colors.status.error.main} style={styles.buttonLabel}>
          {rejectLabel}
        </Text>
      </Pressable>
    </Box>
  );
}

interface CompanySelectionApprovalProps {
  readonly companies: readonly ApprovalCompany[];
  readonly disabled: boolean;
  readonly onApprove: (itemIds: readonly string[]) => void;
  readonly onReject: () => void;
}

function CompanySelectionApproval({
  companies,
  disabled,
  onApprove,
  onReject,
}: CompanySelectionApprovalProps) {
  const [dropped, setDropped] = useState<ReadonlySet<string>>(() => new Set());
  const kept = companies.filter((company) => !dropped.has(company.id)).map(({ id }) => id);

  const keep = (companyId: string) =>
    setDropped((current) => {
      const next = new Set(current);
      next.delete(companyId);
      return next;
    });
  const drop = (companyId: string) => setDropped((current) => new Set(current).add(companyId));

  return (
    <>
      <CompanyApprovalRows
        companies={companies}
        dropped={dropped}
        disabled={disabled}
        onKeep={keep}
        onDrop={drop}
      />
      <ApprovalButtons
        approveLabel={`Create ${kept.length} ${kept.length === 1 ? 'lead' : 'leads'}`}
        rejectLabel="Reject all"
        disabled={disabled}
        approveDisabled={disabled || kept.length === 0}
        onApprove={() => {
          if (kept.length > 0) onApprove(kept);
        }}
        onReject={onReject}
      />
    </>
  );
}

export function PendingActionCard({
  action,
  isResolving = false,
  onApprove,
  onReject,
}: PendingActionCardProps) {
  const expiresAtMs = new Date(action.expiresAt).getTime();
  const [remainingMs, setRemainingMs] = useState(() => expiresAtMs - Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingMs(expiresAtMs - Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, [expiresAtMs]);

  const label = getToolFriendlyLabel(action.toolName);
  const expired = remainingMs <= 0;
  const countdownLabel = expired ? 'expired' : formatRemaining(remainingMs);
  const disabled = isResolving || expired;
  const companies =
    action.toolName === 'create_leads_from_research' ? readApprovalCompanies(action.input) : [];

  return (
    <Box
      testID={`pending-action-card-${action.pendingActionId}`}
      accessibilityRole="alert"
      accessibilityLabel={`Approval needed: ${label}. Expires in ${countdownLabel}.`}
      style={styles.card}
    >
      <Box flexDirection="row" alignItems="center" gap="xs">
        <Clock size={14} color={colors.status.warning.main} strokeWidth={2} />
        <Text variant="caption" color={colors.text.primary} style={styles.label} numberOfLines={2}>
          Approval needed: {label}
        </Text>
      </Box>
      <Text variant="caption" color={colors.text.tertiary} style={styles.countdown}>
        {expired ? 'Expired — awaiting automatic resolution' : `Expires in ${countdownLabel}`}
      </Text>
      {companies.length > 0 ? (
        <CompanySelectionApproval
          companies={companies}
          disabled={disabled}
          onApprove={onApprove}
          onReject={onReject}
        />
      ) : (
        <>
          <ActionInputSummary toolName={action.toolName} input={action.input} />
          <ApprovalButtons
            approveLabel="Approve"
            rejectLabel="Reject"
            disabled={disabled}
            onApprove={() => onApprove()}
            onReject={onReject}
          />
        </>
      )}
    </Box>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderColor: colors.status.warning.border,
    backgroundColor: colors.status.warning.bg,
    borderRadius: tokens.radius.md,
    paddingHorizontal: 14,
    paddingVertical: 10,
    marginHorizontal: 16,
    marginTop: 8,
    gap: 6,
  },
  label: {
    fontWeight: '600',
    flexShrink: 1,
  },
  countdown: {
    marginLeft: 20,
  },
  summary: {
    marginLeft: 20,
    gap: 2,
  },
  summaryLine: {
    flexShrink: 1,
  },
  actions: {
    marginTop: 4,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    minHeight: 36,
    borderRadius: tokens.radius.sm,
    borderWidth: 1,
  },
  approveButton: {
    borderColor: colors.status.success.main,
  },
  rejectButton: {
    borderColor: colors.status.error.main,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonLabel: {
    fontWeight: '600',
  },
});
